import { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Badge from '@mui/material/Badge'
import Input from 'components/Input'
import Icon from 'components/Icon'
import Type from 'components/Type'
import Button from 'components/Button'
import TextButton from 'components/TextButton'
import TelephoneInput from 'components/TelephoneInput'
import Divider from 'components/Divider'
import Paper from 'components/Paper'
import Currency from 'components/Currency'
import InfoButton from 'components/InfoButton'
import I18nAddressForm from 'components/CommonUI/I18nAddressForm'
import { UserStoredCreditCard } from 'components/CommonUI/StoredCreditCard'
import { formatNumber } from 'utils/helpers'

const Payment = ({
  totalDue,
  values,
  onChange,
  creditCardInputProps,
  hideBilling,
  lockCountry,
  hideCost,
  hidePhone,
  onEditModeChange,
  hidePoints,
  surcharge,
}) => {
  const user = useSelector(({ global }) => global.user)
  const [editMode, setEditMode] = useState(false)
  const [ccData, setCCData] = useState(null)
  const savingsTotals = useSelector(({ global }) => global.savingsTotals) || {}
  const {
    bank_actual_total,
    donde_credits_total,
    company_credits_total,
  } = savingsTotals
  const [dondeFunds, setDondeFunds] = useState('')
  const [dondeFundsApplied, setDondeFundsApplied] = useState('')
  const [companyPointsApplied, setCompanyPointsApplied] = useState('')
  const [dondePointsApplied, setDondePointsApplied] = useState('')
  const [fullPointsApplied, setFullPointsApplied] = useState('')
  const remainingPurchaseTotal = +totalDue - +dondeFundsApplied - +companyPointsApplied - +dondePointsApplied
  const remainingPurchaseTotalSurcharge = remainingPurchaseTotal * surcharge
  const remainingPurchaseTotalWithSurcharge = remainingPurchaseTotal * (1 + surcharge)
  const isAmountCoveredByWallet = remainingPurchaseTotal <= 0
  const bp = editMode ? 'xs' : 'xx'

  const isApplyFundsDisabled = +dondeFunds > +bank_actual_total
  const isApplyPointsDisabled = +fullPointsApplied > +company_credits_total + +donde_credits_total

  const isSaveDisabled = 
    isApplyFundsDisabled ||
    isApplyPointsDisabled

  const hasAppliedFunds = 
    +dondeFundsApplied > 0 || 
    +companyPointsApplied > 0 ||
    +dondePointsApplied > 0
    
  const isPaymentValid = 
    +dondeFundsApplied + +companyPointsApplied + +dondePointsApplied === +totalDue || !!ccData

  const handleEditMode = data => {
    setEditMode(data)
    onEditModeChange?.(data)
  }

  const addToValues = data => {
    onChange({
      ...values,
      ...data,
      isPaymentValid,
    })
  }

  const applyFunds = () => {
    if (!isApplyFundsDisabled) {
      setDondeFundsApplied(Math.min(+dondeFunds, +totalDue))
    }
  }

  const applyPoints = () => {
    if (!isApplyPointsDisabled) {
      const tempCompanyPoints = hidePoints ? 0 : Math.min(+fullPointsApplied, +company_credits_total) || 0
      const tempPoints = hidePoints ? 0 : Math.min(+fullPointsApplied - tempCompanyPoints, +donde_credits_total) || 0
      setCompanyPointsApplied(tempCompanyPoints.toString())
      setDondePointsApplied(tempPoints.toString())
    }
  }

  useEffect(() => {
    const companyPoints = hidePoints ? 0 : Math.min(+totalDue, +company_credits_total)
    const points = hidePoints ? 0 : Math.min(+totalDue - companyPoints, +donde_credits_total)
    const wallet = Math.min(+totalDue - companyPoints - points, +bank_actual_total)
    setDondeFunds(wallet ? wallet : '')
    setCompanyPointsApplied(companyPoints ? companyPoints : '')
    setDondePointsApplied(points ? points : '')
    setDondeFundsApplied(wallet ? wallet : '')
    setFullPointsApplied(companyPoints + points)
  }, [
    hidePoints,
    bank_actual_total, 
    company_credits_total, 
    donde_credits_total, 
    totalDue,
  ])

  useEffect(() => {
    onChange({
      ...values,
      phone: values?.phone?.length ? values.phone : user.phone || '',
      transactionFee: (+dondeFundsApplied > 0 || +companyPointsApplied > 0 || +dondePointsApplied > 0) ? remainingPurchaseTotalSurcharge : 0,
      cardAmount: remainingPurchaseTotal,
      dondeAmount: +dondeFundsApplied,
      companyPointsAmount: +companyPointsApplied,
      dondePointsAmount: +dondePointsApplied,
      ccData,
      isPaymentValid,
    })
  }, [
    user.phone,
    dondeFundsApplied, 
    companyPointsApplied, 
    dondePointsApplied, 
    remainingPurchaseTotal, 
    remainingPurchaseTotalSurcharge,
    ccData,
    isPaymentValid,
    hidePoints,
  ])

  return (
    <Stack>
      {/* <Helmet>
        <style>
          {`
            @container payment (min-width: 350px) {
              .payment-card {
                
              }
            }
          `}
        </style>
      </Helmet> */}
      <Stack
        gap={3}
      >
        {!hideCost && (
          <Type
            variant="body"
            color="text.secondary"
          >
            Total cost: <strong><Currency amount={totalDue} /></strong>
          </Type>
        )}
        <Stack>
          <Stack
            sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'space-between' })}
            mb={2}
          >
            <Stack
              sx={theme => theme.internalMixins.columns({ gap: .5 })}
            >
              <Badge
                className="static"
                color="secondary"
                badgeContent="1"
              />
              <Type 
                variant="p"
                color="text.secondary"
              >
                Pay with your Dónde Wallet
              </Type>
            </Stack>
            {!editMode && (
              <TextButton
                color="tertiary"
                onClick={() => handleEditMode(true)}
              >
                Edit
              </TextButton>
            )}
          </Stack>
          <Stack
            gap={1}
          >
            {!hidePoints && (
              <Paper
                outlined
                round="extra"
                borderWidth="main"
                sx={{
                  containerType: 'inline-size',
                  containerName: 'payment',
                }}
              >
                <Stack
                  p={1.5}
                  gap={1}
                >
                  <Stack
                    sx={{
                      display: 'grid',
                      gridAutoFlow: {
                        [bp]: 'column',
                      },
                      gap: 1,
                      alignItems: 'center',
                      alignContent: 'center',
                      justifyContent: {
                        [bp]: 'space-between',
                      },
                    }}
                  >
                    <Stack
                      sx={theme => theme.internalMixins.columns({ gap: .5 })}
                    >
                      <Icon
                        icon="DondePointCoin"
                        width="20"
                        height="20"
                      />
                      <Stack>
                        <Stack
                          sx={theme => theme.internalMixins.columns({ gap: .5 })}
                        >
                          <Type
                            variant={editMode ? 'labelMicroMedium' : 'data'}
                            color="text.secondary"
                            blend={7}
                          >
                            {editMode ? 'Available Dónde Points' : 'Applied Rewards'}
                          </Type>
                          <InfoButton inline>
                            <Type variant="labelMicroMedium">
                              Dónde Points are our rewards currency. You can earn Dónde Points when you hit saving milestones, spend on qualified bookings, and engage in our occasional reward giveaway events. You can apply Dónde Points on any in-app bookings or when you create a virtual card for online travel purchases. Dónde Points are not redeemable for cash.
                            </Type>
                          </InfoButton>
                        </Stack>
                        {editMode && (
                          <Type
                            variant="dataBoldTertiaryUpper"
                            color="text.secondary"
                            blend={7}
                          >
                            ${formatNumber(+company_credits_total + +donde_credits_total, true)} pts
                          </Type>
                        )}
                      </Stack>
                    </Stack>
                    {editMode && (
                      <Box
                        width="100%"
                        maxWidth={{
                          [bp]: 125,
                        }}
                      >
                        <Input
                          mask="currency_no_prefix"
                          icon={<strong>-$</strong>}
                          iconColor="mainCool"
                          padLeft={2.5}
                          removeMaskOnChange
                          fluid
                          forceError={isApplyPointsDisabled}
                          value={fullPointsApplied}
                          onChange={setFullPointsApplied}
                        />
                      </Box>
                    )}
                    {!editMode && (
                      <Type
                        variant="dataBoldTertiaryUpper"
                        color="text.secondary"
                        blend={7}
                      >
                        -
                        <Currency
                          amount={+companyPointsApplied + +dondePointsApplied}
                        />
                      </Type>
                    )}
                  </Stack>
                  <Stack
                    sx={theme => theme.internalMixins.columns({ justify: 'space-between', gap: 1 })}
                  >
                    <Type
                      variant="dataMicro"
                      color="text.secondary"
                      blend={7}
                    >
                      {editMode ? 'Available ' : ''}Company Rewards
                    </Type>
                    <Type
                      variant="dataMicro"
                      color="text.secondary"
                      blend={7}
                    >
                      ({formatNumber(editMode ? +company_credits_total : +companyPointsApplied, true)} pts)
                    </Type>
                  </Stack>
                  <Stack
                    sx={theme => theme.internalMixins.columns({ justify: 'space-between', gap: 1 })}
                  >
                    <Type
                      variant="dataMicro"
                      color="text.secondary"
                      blend={7}
                    >
                      {editMode ? 'Available ' : ''}Dónde Rewards
                    </Type>
                    <Type
                      variant="dataMicro"
                      color="text.secondary"
                      blend={7}
                    >
                      ({formatNumber(editMode ? +donde_credits_total : +dondePointsApplied, true)} pts)
                    </Type>
                  </Stack>
                </Stack>
              </Paper>
            )}
            <Paper
              outlined
              round="extra"
              borderWidth="main"
              sx={{
                containerType: 'inline-size',
                containerName: 'payment',
              }}
            >
              <Stack
                p={1.5}
                gap={1}
              >
                <Stack
                  sx={{
                    display: 'grid',
                    gridAutoFlow: {
                      [bp]: 'column',
                    },
                    gap: 1,
                    alignItems: 'center',
                    alignContent: 'center',
                    justifyContent: {
                      [bp]: 'space-between',
                    },
                  }}
                >
                  <Stack
                    sx={theme => theme.internalMixins.columns({ gap: .5 })}
                  >
                    <Icon
                      icon="Coin"
                      width="20"
                      height="20"
                    />
                    <Stack>
                      <Type
                        variant={editMode ? 'labelMicroMedium' : 'data'}
                        color="text.secondary"
                        blend={7}
                      >
                        {editMode ? 'Available Cash Savings' : 'Applied Cash Savings'}
                      </Type>
                      {editMode && (
                        <Type
                          variant="dataBoldTertiaryUpper"
                          color="text.secondary"
                          blend={7}
                        >
                          <Currency
                            amount={+bank_actual_total}
                          />
                        </Type>
                      )}
                    </Stack>
                  </Stack>
                  {editMode && (
                    <Box
                      width="100%"
                      maxWidth={{
                        [bp]: 125,
                      }}
                    >
                      <Input
                        mask="currency_no_prefix"
                        icon={<strong>-$</strong>}
                        iconColor="mainCool"
                        padLeft={2.5}
                        removeMaskOnChange
                        fluid
                        forceError={isApplyFundsDisabled}
                        value={dondeFunds}
                        onChange={setDondeFunds}
                      />
                    </Box>
                  )}
                  {!editMode && (
                    <Type
                      variant="dataBoldTertiaryUpper"
                      color="text.secondary"
                      blend={7}
                    >
                      -
                      <Currency
                        amount={+dondeFundsApplied}
                      />
                    </Type>
                  )}
                </Stack>
              </Stack>
            </Paper>
            {!editMode && (
              <Paper
                round="more"
                noTransition
                variant={isAmountCoveredByWallet ? 'duoA' : 'duoError'}
              >
                <Stack
                  sx={theme => theme.internalMixins.columns({ gap: 1, justify: 'space-between' })}
                  py={1}
                  px={2}
                >
                  <Type 
                    variant="data" 
                  >
                    Remaining purchase total:
                  </Type>
                  <Type
                    variant="dataBoldTertiaryUpper"
                  >
                    <Currency amount={remainingPurchaseTotal} />
                  </Type>
                </Stack>
              </Paper>
            )}
            {editMode && (
              <Stack
                sx={theme => theme.internalMixins.columns({ gap: 1 })}
              >
                <Button
                  outlined
                  size="shorter"
                  disabled={isSaveDisabled}
                  onClick={() => {
                    applyFunds()
                    applyPoints()
                    handleEditMode(false)
                  }}
                >
                  Save Changes
                </Button>
                <TextButton
                  color="neutralCool_700"
                  onClick={() => {
                    handleEditMode(false)
                  }}
                >
                  Cancel
                </TextButton>
              </Stack>
            )}
          </Stack>
        </Stack>
        {!isAmountCoveredByWallet && (
          <Divider color="neutralCool_300" />
        )}
        {!isAmountCoveredByWallet && (
          <Stack
            gap={2}
          >
            <Stack
              gap={.5}
            >
              <Stack
                sx={theme => theme.internalMixins.columns({ gap: .5 })}
              >
                <Badge
                  className="static"
                  color="secondary"
                  badgeContent="2"
                />
                <Type 
                  variant="p"
                  color="text.secondary"
                >
                  Pay remaining <Box display="inline" color="text.error"><strong><Currency amount={hasAppliedFunds ? remainingPurchaseTotalWithSurcharge : remainingPurchaseTotal} /></strong></Box> with a credit card
                </Type>
              </Stack>
              {surcharge > 0 && (
                <Type variant="dataSmall" color="text.secondary" blend={7}>
                  Split payments incur a {surcharge * 100}% surcharge on the portion paid by credit card.
                </Type>
              )}
            </Stack>
            <UserStoredCreditCard
              {...creditCardInputProps}
              onSetCCData={setCCData}
            />
          </Stack>
        )}
        {!hidePhone && (
          <TelephoneInput
            label="Phone Number"
            defaultNumber={user.phone || ''}
            defaultCountryCode={user.phone_country || ''}
            onChange={(val, data) => {
              addToValues({
                phone: data.isValid ? data.countryCode + data.number : '',
              })
            }}
          />
        )}
      </Stack>

      {!hideBilling && !isAmountCoveredByWallet && (
        <Stack
          mt={3}
          gap={2}
        >
          <Type
            variant="labelSmall"
          >
            Billing Address
          </Type>
          <I18nAddressForm
            lockCountry={lockCountry}
            onChange={onChange}
          />
        </Stack>
      )}
    </Stack>
  )
}

Payment.propTypes = {
  totalDue: PropTypes.number,
  values: PropTypes.object,
  onChange: PropTypes.func,
  creditCardInputProps: PropTypes.object,
  hideBilling: PropTypes.bool,
  hideFullCC: PropTypes.bool,
  requirePhone: PropTypes.bool,
  hideCost: PropTypes.bool,
  lockCountry: PropTypes.string,
  hidePhone: PropTypes.bool,
  hidePoints: PropTypes.bool,
  surcharge: PropTypes.number,
}

Payment.defaultProps = {
  totalDue: 0,
  values: {},
  onChange: () => {},
  creditCardInputProps: {},
  surcharge: .02,
}

export default Payment